import { Stack, Typography } from '@mui/material';
import { isUndefined } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { useDualGrantorOptions } from '@/hooks/useDualGrantorOptions';
import { useReportError } from '@/hooks/useReportError';
import { EntityType } from '@/modules/entities/types/EntityType';
import { getEntityKindFromEntityType } from '@/modules/entities/utils/getEntityKindFromEntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { EntityKind, EntityStage } from '@/types/schema';
import { getEntitySelectOptions } from '@/utils/entities/getEntitySelectOptions';
import { getNodes } from '@/utils/graphqlUtils';

import { useHypotheticalInsuranceDestinationsQuery } from '../graphql/HypotheticalInsuranceDestinations.generated';
import { HYPOTHETICAL_INSURANCE_ENTITY_KIND_OPTIONS } from '../HypotheticalILITModal.constants';
import { useHypotheticalILITModalContext } from '../HypotheticalILITModal.context';
import {
  HypotheticalILITFormModalShape,
  HypotheticalILITFormModalShapePaths,
} from '../HypotheticalILITModal.types';

function useEntityTypeOptions(): TypeaheadSelectInputOption<EntityKind>[] {
  /*
   * To re-use the existing copy & logic from the entity form (which uses
   * EntityType instead of the backend's EntityKind), we have to round-trip
   * the values EntityKind -> EntityType -> EntityKind.
   */
  return useMemo(() => {
    const entityTypeOptions =
      HYPOTHETICAL_INSURANCE_ENTITY_KIND_OPTIONS.map<EntityType>(
        getEntityTypeFromEntityKind
      );
    const kindOptions = getEntitySelectOptions(entityTypeOptions);

    const options = kindOptions.map<TypeaheadSelectInputOption<EntityKind>>(
      (option) => ({
        ...option,
        value: getEntityKindFromEntityType(option.value),
      })
    );

    return options;
  }, []);
}

export function HypotheticalTrustDetailsSubform() {
  const { control } = useFormContext<HypotheticalILITFormModalShape>();

  const entityTypeOptions = useEntityTypeOptions();
  const grantorOptions = useDualGrantorOptions();

  return (
    <Card variant="filled" sx={{ p: 2, pb: 3 }}>
      <Typography variant="title-s" sx={{ pb: 1 }}>
        Hypothetical trust details
      </Typography>
      <FormLayoutRow>
        <FormLayoutItem width={12}>
          <FormAwareTypeaheadSelectInput<HypotheticalILITFormModalShape>
            control={control}
            fieldName={
              'destinationEntityType' as const satisfies HypotheticalILITFormModalShapePaths
            }
            label="Trust type"
            required
            options={entityTypeOptions}
            groupBy={(option) => option.groupName ?? ''}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem width={6}>
          <FormAwareTextInput<HypotheticalILITFormModalShape>
            control={control}
            fieldName={
              'destinationEntityName' as const satisfies HypotheticalILITFormModalShapePaths
            }
            label="Trust name"
            required
          />
        </FormLayoutItem>
        <FormLayoutItem width={6}>
          <FormAwareSelectInput<HypotheticalILITFormModalShape>
            control={control}
            fieldName={
              'destinationEntityGrantor' as const satisfies HypotheticalILITFormModalShapePaths
            }
            label="Grantor"
            required
            options={grantorOptions}
          />
        </FormLayoutItem>
      </FormLayoutRow>
    </Card>
  );
}

export function HypotheticalILITPolicyOwnerDetailsBody() {
  const { control, setValue } =
    useFormContext<HypotheticalILITFormModalShape>();
  const { householdId: householdID } = useHypotheticalILITModalContext();

  const [createANewHypotheticalTrust] = useWatch({
    control,
    name: ['_createANewHypotheticalTrust'],
  });

  const { data, loading, error } = useHypotheticalInsuranceDestinationsQuery({
    variables: { householdID },
    fetchPolicy: 'cache-and-network',
  });

  useReportError('Hypothetical insurance transfer destinations query', error);

  const destinationOptions: TypeaheadSelectInputOption<string>[] = useMemo<
    TypeaheadSelectInputOption<string>[]
  >(
    () =>
      getNodes(data?.entities)
        .map<TypeaheadSelectInputOption<string>>((entity) => ({
          value: entity.id,
          display: entity.subtype.displayName,
          groupName:
            entity.stage === EntityStage.Draft
              ? 'Draft Entities'
              : 'Existing Entities',
        }))
        .sort(
          (a, b) =>
            (b.groupName ?? '').localeCompare(a.groupName ?? '') ||
            a.display.localeCompare(b.display)
        ),
    [data?.entities]
  );

  const previousCreateANewHypotheticalTrust = usePrevious(
    createANewHypotheticalTrust
  );
  useEffect(() => {
    if (isUndefined(previousCreateANewHypotheticalTrust)) return;
    if (!previousCreateANewHypotheticalTrust && createANewHypotheticalTrust) {
      setValue(
        'destinationEntityId' as const satisfies HypotheticalILITFormModalShapePaths,
        ''
      );
    }
  }, [
    createANewHypotheticalTrust,
    previousCreateANewHypotheticalTrust,
    setValue,
  ]);

  return (
    <Card variant="filled" sx={{ p: 2, pb: 4 }}>
      <Stack spacing={0.5} sx={{ pb: 3 }}>
        <Typography variant="title-l">Policy owner details</Typography>
        <Typography variant="body-xs">
          Select an existing trust or create a new hypothetical trust
        </Typography>
      </Stack>
      <FormLayoutRow>
        <FormLayoutItem width={12}>
          <FormAwareTypeaheadSelectInput<HypotheticalILITFormModalShape>
            control={control}
            label="Policy owner"
            options={destinationOptions}
            fieldName={
              'destinationEntityId' as const satisfies HypotheticalILITFormModalShapePaths
            }
            disabled={loading || createANewHypotheticalTrust}
            required={!createANewHypotheticalTrust}
            groupBy={(option) => option.groupName ?? ''}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem width={12}>
          <FormAwareSwitch<HypotheticalILITFormModalShape>
            control={control}
            fieldName={
              '_createANewHypotheticalTrust' as const satisfies HypotheticalILITFormModalShapePaths
            }
            label="Create a new hypothetical trust"
            labelPosition="right"
          />
        </FormLayoutItem>
      </FormLayoutRow>
      {createANewHypotheticalTrust && <HypotheticalTrustDetailsSubform />}
    </Card>
  );
}
